import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { parentApi } from "../../lib/api";
import { Card, Button, Coins, Pill } from "../../components/ui";
import Avatar from "../../components/Avatar";
import Mascot from "../../components/Mascot";
import Icon from "../../components/Icon";
import { displayColor } from "../../lib/theme";

function ProgressRing({ value, goal, color }) {
  const pct = Math.min(100, goal ? (value / goal) * 100 : 0);
  const r = 26, c = 2 * Math.PI * r;
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="shrink-0">
      <circle cx="32" cy="32" r={r} fill="none" stroke="#E2E8F0" strokeWidth="7" />
      <circle cx="32" cy="32" r={r} fill="none" stroke={displayColor(color)} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} transform="rotate(-90 32 32)"
        style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      <text x="32" y="37" textAnchor="middle" fontSize="15" fontWeight="800" fill="#25204B">{Math.round(pct)}%</text>
    </svg>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([parentApi.get("/children"), parentApi.get("/leaderboard")]);
      setChildren(c.data.children);
      setBoard(l.data.leaderboard);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalEarned = children.reduce((s, k) => s + k.stats.earned, 0);
  const totalBalance = children.reduce((s, k) => s + k.stats.balance, 0);

  if (loading) return <div className="grid place-items-center py-20"><div className="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="parent-home">
      <div>
        <h1 className="font-heading font-black text-3xl text-ink">Today at a glance</h1>
        <p className="text-ink-soft font-medium">Approve work, award points, keep the streaks alive.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Kids", value: children.length, icon: "users", tone: "primary" },
          { label: "Coins earned", value: totalEarned, icon: "star", tone: "sunny" },
          { label: "Coins to spend", value: totalBalance, icon: "wallet", tone: "coral" },
        ].map((s) => (
          <Card key={s.label} className="p-4">
            <Icon name={s.icon} className={`mb-2 ${s.tone === "sunny" ? "text-sunny-fg" : s.tone === "coral" ? "text-coral" : "text-primary"}`} width={22} height={22} />
            <div className="font-heading font-black text-2xl text-ink leading-none">{s.value}</div>
            <div className="text-xs font-bold text-ink-muted mt-1">{s.label}</div>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-heading font-extrabold text-xl text-ink">Your kids</h2>
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/family")} data-testid="home-manage-kids">Manage <Icon name="chevron-right" width={16} height={16} /></Button>
      </div>

      {children.length === 0 ? (
        <Card className="p-8 text-center">
          <Mascot size={96} className="mx-auto mb-2" />
          <p className="font-bold text-ink mb-4">Add your first kid to get started.</p>
          <Button onClick={() => navigate("/app/family")} data-testid="home-add-first-kid">Add a kid</Button>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {children.map((k, i) => (
            <motion.div key={k.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card kid className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar avatar={k.avatar} color={k.color} size={52} />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold text-lg text-ink leading-tight">{k.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Pill tone="sunny"><Icon name="coins" width={12} height={12} /> {k.stats.balance}</Pill>
                      {k.stats.streak > 0 && <Pill tone="coral"><Icon name="flame" width={12} height={12} /> {k.stats.streak}d</Pill>}
                    </div>
                  </div>
                  <ProgressRing value={k.stats.weekly_points} goal={k.stats.weekly_goal} color={k.color} />
                </div>
                <div className="flex items-center justify-between text-sm font-bold text-ink-soft">
                  <span>{k.stats.weekly_points}/{k.stats.weekly_goal} this week</span>
                  <span className="flex items-center gap-1"><Icon name="award" width={14} height={14} className="text-sunny-fg" /> {k.stats.badges.length} badges</span>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {board.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4"><Icon name="trophy" className="text-sunny-fg" width={22} height={22} /><h2 className="font-heading font-extrabold text-xl text-ink">Weekly leaderboard</h2></div>
          <div className="space-y-2">
            {board.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 py-2">
                <div className={`w-7 h-7 rounded-full grid place-items-center font-black text-sm ${i === 0 ? "bg-sunny text-sunny-fg" : i === 1 ? "bg-slate-200 text-ink" : i === 2 ? "bg-coral/30 text-coral-dark" : "bg-slate-100 text-ink-muted"}`}>{i + 1}</div>
                <Avatar avatar={r.avatar} color={r.color} size={36} ring={false} />
                <span className="font-bold text-ink flex-1">{r.name}</span>
                <Coins amount={r.weekly_points} className="text-ink" />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
